package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.FileDto
import dev.lingvo.shared.MtJobDto
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ReadinessDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.components.ProjectIcon
import dev.lingvo.site.components.ShellToastHost
import dev.lingvo.site.components.automation.PretranslateConfirm
import dev.lingvo.site.components.automation.PretranslateScope
import dev.lingvo.site.components.automation.showJobToast
import dev.lingvo.site.components.automation.showPretranslateStarted
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LArrowRightIcon
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LBracesIcon
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LChevronDownIcon
import dev.lingvo.site.ds.LCodeBlock
import dev.lingvo.site.ds.LDownloadIcon
import dev.lingvo.site.ds.LEarthIcon
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LFilesIcon
import dev.lingvo.site.ds.LFilterIcon
import dev.lingvo.site.ds.LHistoryIcon
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LKeyIcon
import dev.lingvo.site.ds.LLanguagesIcon
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMenu
import dev.lingvo.site.ds.LMenuEntry
import dev.lingvo.site.ds.LMenuItem
import dev.lingvo.site.ds.LRefreshIcon
import dev.lingvo.site.ds.LSplitButton
import dev.lingvo.site.ds.LStatTile
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LUploadIcon
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.ToastState
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.util.downloadText
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Page("/app/projects/{slug}")
@Layout(".components.layouts.AppLayout")
@Composable
fun ProjectOverviewPage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()

    // A blank slug means the URL is wrong, not that a request failed — it never reaches the API,
    // so it must not surface through the same failure state a slow or broken server would.
    // `require(...)` used to throw "Project slug is missing.", a developer sentence, straight
    // through to `AsyncFailureState`.
    if (slug.isBlank()) {
        LEmptyState(
            title = t("app.projects.slug.index.this_project_link_is_incomplete", "This project link is incomplete"),
            description = t("app.projects.slug.index.open_it_from_your_project_list", "Open it from your project list instead."),
            modifier = Modifier.fillMaxWidth(),
            icon = LEmptyStateIcon.FORBIDDEN,
        ) {
            LButton(
                label = t("app.projects.slug.index.go_to_your_project_list", "Go to your project list"),
                onClick = { ctx.router.tryRoutingTo("/app/projects") },
                variant = LButtonVariant.PRIMARY,
            )
        }
        return
    }

    var refreshKey by remember { mutableStateOf(0) }
    val directoryScope = rememberCoroutineScope()
    val authoritySubject = liveProjectRoute(slug)?.authoritySubject(Session.organizations)
    // `loadReloadable`, not `loadAsync`: a refresh on this page must not blank the page. See the
    // KDoc on `loadReloadable` — passing a counter as a `loadAsync` key discards the value before
    // the request is sent, so the most frequent interaction here replaced what the reader was
    // reading with skeleton bars and then reassembled it with nothing marking what changed.
    val reloadable = loadReloadable(slug, authoritySubject, reloadToken = refreshKey) {
        loadProjectOverview(
            slug = slug,
            refreshProject = refreshKey > 0,
            directoryScope = directoryScope,
        )
    }

    LAsyncContent(
        reloadable.state,
        rows = 7,
        loadingLabel = t("app.projects.slug.index.loading_project_overview", "Loading project overview"),
        // Names the project rather than "this page", which is what the KDoc on
        // `AsyncFailureState` argues for and what a refusal on a shared link needs to say.
        subject = t("app.projects.slug.index.this_project", "this project"),
        onRetry = { refreshKey++ },
    ) { overview ->
        ProjectOverviewContent(
            data = overview,
            authority = ProjectAuthority.resolve(overview.project, Session.organizations),
            onNavigate = { path -> ctx.router.tryRoutingTo(path) },
            onRefresh = { refreshKey++ },
            refreshKey = refreshKey,
            refreshing = reloadable.refreshing,
            refreshError = reloadable.refreshError?.message,
        )
    }
}


/**
 * @param describeRun how the receipt toast learns the run's real size. A run this page attached to
 *   through a `409 job_already_running` was scoped by somebody else, so the count can only come
 *   from the server.
 * @param exportSourceFile what the file row's download entry actually does. Injected so a test can
 *   watch the press without a network or a save dialog.
 */
@Composable
internal fun ProjectOverviewContent(
    data: ProjectOverviewData,
    authority: ProjectAuthority,
    onNavigate: (String) -> Unit,
    onRefresh: () -> Unit,
    refreshKey: Int,
    refreshing: Boolean,
    refreshError: String?,
    describeRun: suspend (projectSlug: String, jobId: String) -> MtJobDto = ApiClient::mtJob,
    exportSourceFile: suspend (FileDto) -> Unit = { file ->
        downloadSourceFile(data.project, file)
    },
) {
    val project = data.project
    val targetLocales = project.locales.filterNot { it == project.sourceLocale }
    val firstTarget = targetLocales.firstOrNull()
    // One page for both directions now. `/data` plans an import before applying it and hosts
    // `ExportCard`; `/import-export` is a redirect to it and no longer worth linking through.
    val dataPath = "/app/projects/${project.slug}/data"
    val primary = primaryAction(project.keyCount, project.slug, firstTarget, authority.role)
    val untranslated = remainingKeys(project)
    // **The header's status comes from the readiness call, not from arithmetic over the progress
    // rows.** "All strings have a value" and "this language can ship" are different questions —
    // QA errors and unreviewed machine output block a release with every string filled in — and
    // deriving the badge here would have made the header disagree with the section below it.
    var readiness by remember(data.actionKey, authority.role) { mutableStateOf<ReadinessDto?>(null) }
    val readinessStatus = readinessBlockingLabel(readiness)
    val readinessShippable = readiness?.locales?.all { it.shippable } == true
    // Every language that still has untranslated strings, which is what the primary action covers.
    val behind = remember(project) { behindLocales(project) }
    var confirmingRun by remember(project.slug) { mutableStateOf(false) }
    // The shell's channel, not `LocalToasts`: this page renders inside the shell in production and
    // inside a bare route in the authority tests, and reading a local that one of them does not
    // provide turns a missing provider into a blank page.
    val toasts = ShellToastHost.state
    val router = rememberPageContext().router
    val actionScope = rememberCoroutineScope()
    // Only the row that was pressed goes busy, and only while its own file is being fetched.
    var exportingFileId by remember(project.slug) { mutableStateOf<String?>(null) }
    val onDownloadSource: (FileDto) -> Unit = { file ->
        if (exportingFileId == null) {
            actionScope.launch {
                exportingFileId = file.id
                try {
                    exportSourceFile(file)
                } catch (cancelled: CancellationException) {
                    // Leaving the page cancels this scope. A download that was never attempted is
                    // not a download that failed.
                    throw cancelled
                } catch (failure: Throwable) {
                    toasts?.show(exportFailureMessage(failure), LToastVariant.DANGER)
                } finally {
                    exportingFileId = null
                }
            }
        }
    }
    Column(Modifier.fillMaxWidth().gap(Space.XXXL)) {
        PageHeader(
            title = project.name,
            // Direction only. The counts live in the stat tiles 20px below — stating them here
            // too put "45 keys - 100 left" directly above "45 Keys" and "100 Untranslated", the
            // same two numbers twice in one eyeful and in two different words.
            subtitle = projectDirectionLine(project),
            // The best available guess, not a declared fact: a project name carries no language of
            // its own, so this is right whenever the name is in the source language. `dir="auto"`
            // applies regardless and fixes RTL layout independently of this.
            titleLang = project.sourceLocale,
            // Identity leads the block. The mark used to sit inside `actions`, beside the buttons,
            // which put the one element that says *which project this is* in the row reserved for
            // what to do about it.
            mark = {
                ProjectIcon(
                    slug = project.slug,
                    name = project.name,
                    iconKey = project.iconKey,
                    accentKey = project.accentKey,
                    hasCustomIcon = project.hasCustomIcon,
                )
            },
            status = readinessStatus?.let { label ->
                {
                    LBadge(
                        label,
                        variant = if (readinessShippable) LBadgeVariant.SUCCESS else LBadgeVariant.WARNING,
                        dot = true,
                    )
                }
            },
            // **The three counts every decision on this page is made from, above the fold.** They
            // were spread across a subtitle, a readiness sentence and a table caption, and the one a
            // developer opens this page for — how many strings are still untranslated — was in none
            // of them as a figure.
            meta = {
                LStatTile(
                    label = t("app.projects.slug.index.keys", "Keys"),
                    value = formatCount(project.keyCount),
                    icon = { LKeyIcon() },
                    href = "/app/projects/${project.slug}/keys",
                    compact = true,
                )
                LStatTile(
                    label = t("app.projects.slug.index.untranslated", "Untranslated"),
                    value = formatCount(untranslated),
                    icon = { LLanguagesIcon() },
                    // Zero is the good outcome and links nowhere: the editor filtered to an empty
                    // set is a promise of work that is not there.
                    href = firstTarget
                        ?.takeIf { untranslated > 0 }
                        ?.let { "/app/projects/${project.slug}/translate/$it" },
                    compact = true,
                )
                LStatTile(
                    label = t("app.projects.slug.index.target_languages", "Target languages"),
                    value = formatCount(targetLocales.size),
                    icon = { LEarthIcon() },
                    href = "/app/projects/${project.slug}/settings?tab=languages"
                        .takeIf { authority.canEditStructure },
                    compact = true,
                )
            },
            actions = {
                // The empty state below carries `primary` itself, so an empty project would show
                // one destination behind two controls: the header's button and the empty state's.
                // The title and the direction line still say something true here —
                // `projectDirectionLine` returns "en → German".
                if (!showsOnlyEmptyState(project.keyCount)) {
                    // The reader owns the repository and translates by machine with their own
                    // provider keys (PRODUCT.md Users, amended 2026-08-25), so the machine run is
                    // primary for an admin and the sparkle names what it is: a job, not a
                    // destination.
                    primary?.let { action ->
                        if (action.spendsCredits) {
                            // **One press for the common run, a menu for the variant that costs
                            // nothing.** Filling from translation memory is a different decision
                            // from buying translations — it reserves no credits and calls no
                            // provider — and it was reachable from nowhere in the product.
                            val entries = pretranslateMenuEntries()
                            LSplitButton(
                                label = action.label,
                                // **The price where the count is.** The primary used to open a form
                                // that asked which languages to run over — a question this page has
                                // already answered on screen. It now prices exactly the languages
                                // that are behind, and only falls back to the form when nothing is:
                                // a run over an empty set is a dialog that costs nothing and says
                                // nothing, and the form is where a different scope is chosen.
                                onClick = {
                                    if (behind.isEmpty()) onNavigate(action.path) else confirmingRun = true
                                },
                                items = entries,
                                onSelect = { index ->
                                    when (index) {
                                        // The scope form. Without this entry a project with behind
                                        // locales — the only case this split button renders in —
                                        // had no route from the header to a run over one tag, one
                                        // file, or a language that is not behind.
                                        0 -> onNavigate(action.path)
                                        1 -> onNavigate("${action.path}?memoryOnly=true")
                                        else -> Unit
                                    }
                                },
                                menuLabel = t(
                                    "app.projects.slug.index.other_ways_to_pre_translate",
                                    "Other ways to pre-translate",
                                ),
                                leadingIcon = { LWandSparklesIcon() },
                            )
                        } else {
                            LButton(
                                label = action.label,
                                onClick = { onNavigate(action.path) },
                                variant = LButtonVariant.PRIMARY,
                                leadingIcon = { LKeyIcon() },
                            )
                        }
                    }
                    // **Beside the control that spends, not floating under the header.** This was
                    // a muted sentence at page level, which made it metadata about the project
                    // rather than a caption on a button — and it rendered on a project with no
                    // keys, stating a balance next to an action that spends nothing. `PRODUCT.md`
                    // asks for reassurance at the point of spend; the point of spend is here.
                    if (primary?.spendsCredits == true) {
                        data.billing?.let { billing ->
                            val remaining =
                                billing.creditsIncluded + billing.creditsGranted - billing.creditsUsed
                            Span(
                                Modifier
                                    .color(ColorMode.current.toSitePalette().textMuted)
                                    .fontSize(TypeScale.Small)
                                    .toAttrs(),
                            ) { Text(creditNote(remaining)) }
                        }
                    }
                    // **No secondary "Translate" here, deliberately.** `firstTarget` is
                    // `project.locales.first()` — an arbitrary language — while the readiness rows
                    // below are sorted worst-first, so this button offered a second, uninformed
                    // opinion about which language matters, without naming the one it meant. Every
                    // row already carries a Translate button that is correctly ranked and says
                    // which language it acts on.
                }
                // The reader's most common act is `lingvo push` then look at this page, and
                // reloading the browser used to be the only way to see what landed.
                //
                // `loading` rather than a spinner over the page: `loadReloadable` keeps the last
                // good value on screen during a re-fetch, so the only thing that should change is
                // this control. It sets `aria-busy`, which is what a non-visual reader gets in
                // place of the movement a sighted one sees.
                LIconAction(
                    label = if (refreshing) t("app.projects.slug.index.refreshing_this_project", "Refreshing this project") else t("app.projects.slug.index.refresh_this_project", "Refresh this project"),
                    onClick = onRefresh,
                    variant = LButtonVariant.SECONDARY,
                    loading = refreshing,
                    // Refresh, not history. The two glyphs were swapped across these two surfaces:
                    // this button re-runs a request and drew a clock, while the activity log — which
                    // is nothing but history — drew a refresh arrow for its empty state.
                ) { LRefreshIcon() }
            },
        )

        if (refreshError != null) {
            LAlert(
                title = t("app.projects.slug.index.showing_the_last_successful_overview", "Showing the last successful overview"),
                description = refreshError,
                modifier = Modifier.fillMaxWidth(),
                variant = LAlertVariant.WARNING,
                // The reader pressed Refresh and it failed. Without this the page silently keeps
                // showing the previous overview and nothing says the new one never arrived.
                live = true,
            )
        }

        if (primary == null && (project.keyCount == 0 || firstTarget == null)) {
            LAlert(
                title = t("app.projects.slug.index.project_setup_is_managed_by_an", "Project setup is managed by an owner or admin"),
                description = overviewSetupGuidance(authority.role),
                modifier = Modifier.fillMaxWidth(),
                variant = LAlertVariant.INFO,
            )
        }

        if (showsOnlyEmptyState(project.keyCount)) {
            // A project with no keys has nothing to say in four sections, and saying it four
            // times produced three differently-worded buttons pointing at one destination.
            LEmptyState(
                title = t("app.projects.slug.index.nothing_here_yet", "Nothing here yet"),
                description = if (authority.canEditStructure) {
                    t("app.projects.slug.index.import_a_localization_file_to_create", "Import a localization file to create this project's keys. The CLI does it in one command, or bring a file in from here.")
                } else {
                    t("app.projects.slug.index.an_owner_or_admin_needs_to", "An owner or admin needs to add the first localization file before translation can begin.")
                },
                modifier = Modifier.fillMaxWidth(),
                icon = LEmptyStateIcon.CODE,
            ) {
                primary?.let { action ->
                    LButton(
                        label = action.label,
                        onClick = { onNavigate(action.path) },
                        variant = LButtonVariant.PRIMARY,
                        leadingIcon = { LUploadIcon() },
                    )
                }
            }
            // `lingvo push` changes project structure, so an empty project only exposes it to a
            // role that may perform the matching import.
            if (authority.canEditStructure) TerminalSection(project)
        } else {
            // Above today's content, because "can we ship" is the question this page exists to
            // answer and the file list is not it.
            ProjectReadinessSection(
                project = project,
                authority = authority,
                actionKey = data.actionKey,
                refreshSignal = refreshKey,
                onLoaded = { readiness = it },
                onNavigate = onNavigate,
            )
            TerminalSection(project)
            FilesSection(
                data = data,
                dataPath = dataPath,
                canEditStructure = authority.canEditStructure,
                downloadingFileId = exportingFileId,
                onDownloadSource = onDownloadSource,
                onNavigate = onNavigate,
            )
            ActivitySection(data.activity, project, firstTarget, dataPath, authority.canEditStructure, onNavigate)
        }

        // Estimate before, receipt after, both on this page. Mounted at page level rather than
        // inside the header's actions so that closing the split button's menu cannot take the
        // dialog down with it; it requests nothing until `confirmingRun`.
        PretranslateConfirm(
            open = confirmingRun,
            slug = project.slug,
            locales = behind,
            scope = PretranslateScope(onlyEmpty = true),
            onStarted = { jobId ->
                // The receipt door, held open until somebody closes it: the run id is shown
                // nowhere else, so a toast that expires leaves a charge with nothing to look up.
                //
                // **The count is the run's, asked of the server.** It used to be `untranslated` —
                // this page's own figure — and `PretranslateConfirm` reports a run attached through
                // `409 job_already_running` through the same callback, so a colleague's run over a
                // different set of languages was announced with the reader's local number.
                if (toasts != null) {
                    actionScope.launch {
                        announcePretranslateRun(toasts, router, project.slug, jobId, describeRun)
                    }
                }
            },
            onClose = { confirmingRun = false },
        )
    }
}

@Composable
private fun TerminalSection(project: ProjectDetailDto) {
    val hint = terminalHint(project.slug, remainingKeys(project), project.keyCount)
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        SectionHeading(
            title = t("app.projects.slug.index.next_in_your_terminal", "Next in your terminal"),
            icon = { LBracesIcon() },
        )
        // The heading already says "Next in your terminal" — a label that repeats it 8px below
        // made the copy button's accessible name "Copy Command to run in your terminal". This one
        // reads as a sentence, and still names which of push or pull it is.
        LCodeBlock(
            hint.command,
            label = if (hint.command.endsWith(" pull")) t("app.projects.slug.index.the_pull_command", "the pull command") else t("app.projects.slug.index.the_push_command", "the push command"),
        )
        Span(
            Modifier
                .color(ColorMode.current.toSitePalette().textMuted)
                .fontSize(TypeScale.Small)
                .toAttrs(),
        ) { Text(hint.note) }
    }
}

@Composable
private fun FilesSection(
    data: ProjectOverviewData,
    dataPath: String,
    canEditStructure: Boolean,
    downloadingFileId: String?,
    onDownloadSource: (FileDto) -> Unit,
    onNavigate: (String) -> Unit,
) {
    val project = data.project
    val keysPath = "/app/projects/${project.slug}/keys"
    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
        SectionHeading(
            title = t("app.projects.slug.index.files", "Files"),
            icon = { LFilesIcon() },
            actions = {
                // The table below names files and counts keys, and until now there was no way from
                // it to the keys themselves — the section that is entirely about where the strings
                // live did not link to them.
                if (project.files.isNotEmpty()) {
                    LButton(
                        label = t("app.projects.slug.index.browse_all_keys", "Browse all keys"),
                        onClick = { onNavigate(keysPath) },
                        variant = LButtonVariant.GHOST,
                        size = LButtonSize.SM,
                        trailingIcon = { LArrowRightIcon() },
                    )
                }
            },
        )
        if (project.files.isEmpty()) {
            LEmptyState(
                title = t("app.projects.slug.index.import_the_first_file", "Import the first file"),
                description = t("app.projects.slug.index.bring_in_a_source_localization_file", "Bring in a source localization file to create keys and translations."),
                modifier = Modifier.fillMaxWidth(),
                icon = LEmptyStateIcon.CODE,
            ) {
                if (canEditStructure) {
                    LButton(
                        label = t("app.projects.slug.index.import_file", "Import file"),
                        onClick = { onNavigate(dataPath) },
                        leadingIcon = { LUploadIcon() },
                    )
                }
            }
        } else {
            LTable(caption = t("app.projects.slug.index.project_localization_files", "Project localization files")) {
                LTableHead {
                    LTableRow {
                        LTableHeaderCell { Text(t("app.projects.slug.index.path", "Path")) }
                        LTableHeaderCell { Text(t("app.projects.slug.index.format", "Format")) }
                        LTableHeaderCell { Text(t("app.projects.slug.index.keys", "Keys")) }
                        LTableHeaderCell { Text(t("app.projects.slug.index.export", "Export")) }
                    }
                }
                LTableBody {
                    project.files.forEach { file ->
                        FileRow(
                            file = file,
                            keysPath = keysPath,
                            dataPath = dataPath,
                            downloading = downloadingFileId == file.id,
                            onDownloadSource = onDownloadSource,
                            onNavigate = onNavigate,
                        )
                    }
                }
            }
        }
    }
}

/**
 * What "Export" on a file row can actually do, as a closed set.
 *
 * The glyph used to lie: a download arrow that navigated to the data page. Either the press
 * downloads something or the control says where it is taking you, and a menu is how one row offers
 * both without a second column of buttons.
 */
internal enum class FileExportChoice { SOURCE_LOCALE, OTHER_LOCALES }

@Composable
private fun FileRow(
    file: FileDto,
    keysPath: String,
    dataPath: String,
    downloading: Boolean,
    onDownloadSource: (FileDto) -> Unit,
    onNavigate: (String) -> Unit,
) {
    var exportOpen by remember(file.id) { mutableStateOf(false) }
    LTableRow {
        LTableHeaderCell(scope = LTableHeaderScope.ROW) {
            Span(Modifier.monoFont().fontSize(TypeScale.BodySmall).toAttrs()) { Text(file.path) }
        }
        LTableCell { LBadge(file.format.displayName) }
        LTableCell {
            // The count is the way into the strings it counts. A number beside a file path is the
            // most obvious thing on this row to press, and it did nothing.
            LLinkButton(
                label = formatCount(file.keyCount),
                path = "$keysPath?fileId=${file.id}",
                accessibleLabel = trf(
                    "app.projects.slug.index.keys_in_file",
                    "{count} keys in {path}",
                    "count" to file.keyCount,
                    "path" to file.path,
                ),
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
            )
        }
        LTableCell {
            val choices = FileExportChoice.entries
            LMenu(
                expanded = exportOpen,
                entries = fileExportEntries(),
                onExpandedChange = { exportOpen = it },
                onSelect = { index ->
                    when (choices.getOrNull(index)) {
                        // **The download glyph stops lying here.** Both arms used to navigate to
                        // the data page, so the entry labelled "Download the source file" beside a
                        // download arrow downloaded nothing and the two entries did the same thing.
                        FileExportChoice.SOURCE_LOCALE -> onDownloadSource(file)
                        FileExportChoice.OTHER_LOCALES -> onNavigate("$dataPath?fileId=${file.id}")
                        null -> Unit
                    }
                },
            ) { onToggle, triggerModifier ->
                LButton(
                    label = t("app.projects.slug.index.export", "Export"),
                    accessibleLabel = trf(
                        "app.projects.slug.index.export_file",
                        "Export {path}",
                        "path" to file.path,
                    ),
                    onClick = onToggle,
                    modifier = triggerModifier,
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                    // The row that was pressed is the only thing that goes busy while its file is
                    // being fetched.
                    loading = downloading,
                    leadingIcon = { LDownloadIcon() },
                    trailingIcon = { LChevronDownIcon() },
                )
            }
        }
    }
}

@Composable
private fun fileExportEntries(): List<LMenuEntry> = listOf(
    LMenuItem(
        t("app.projects.slug.index.download_the_source_file", "Download the source file"),
        icon = { LDownloadIcon() },
    ),
    LMenuItem(
        t("app.projects.slug.index.other_languages_and_formats", "Other languages and formats"),
        icon = { LArrowRightIcon() },
    ),
)

/**
 * The two ways to pre-translate that the primary press does not already cover.
 *
 * "Choose scope…" is the scope form (`/pretranslate`) — a run over one tag, one file, or a
 * language that is not behind — and it is the only route to it from this header, because the
 * primary opens the estimate for the behind locales instead. "Fill from translation memory only"
 * reserves no credits and calls no provider.
 *
 * A function rather than a `val`: every label goes through the catalogue, and a top-level value
 * would freeze in English at class-init, before any catalogue has been fetched.
 */
@Composable
private fun pretranslateMenuEntries(): List<LMenuEntry> = listOf(
    LMenuItem(
        // Named for what it is rather than for where it lives, exactly as the dashboard card's
        // menu names the same destination.
        t("app.projects.slug.index.choose_scope", "Choose scope\u2026"),
        icon = { LFilterIcon() },
        description = t(
            "app.projects.slug.index.a_tag_one_file_or_another",
            "A tag, one file, or another language.",
        ),
    ),
    LMenuItem(
        t("app.projects.slug.index.fill_from_translation_memory_only", "Fill from translation memory only"),
        icon = { LHistoryIcon() },
        description = t(
            "app.projects.slug.index.reuses_what_this_workspace_has_already",
            "Reuses what this workspace has already translated. Costs no credits.",
        ),
    ),
)

/**
 * The receipt toast for a run that has just started, sized by the server rather than by this page.
 *
 * `JobToast` requires the count be "the run's real scope as the server priced it, never a rounded
 * or invented number", and this page cannot know it: `PretranslateConfirm` reports a run attached
 * through `409 job_already_running` through the same callback as a fresh one, and that run was
 * scoped by whoever started it. So the run is asked to describe itself first — the same order
 * `ReadinessRunStore.attach` announces in — and when it will not, the toast still opens the receipt
 * door but claims no number at all.
 */
internal suspend fun announcePretranslateRun(
    toasts: ToastState,
    router: Router,
    projectSlug: String,
    jobId: String,
    describeRun: suspend (projectSlug: String, jobId: String) -> MtJobDto,
) {
    val total = try {
        describeRun(projectSlug, jobId).total
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (failure: Throwable) {
        null
    }
    if (total != null) {
        showPretranslateStarted(toasts, router, projectSlug, jobId, total)
    } else {
        showJobToast(
            toasts = toasts,
            router = router,
            projectSlug = projectSlug,
            jobId = jobId,
            message = tr("app.projects.slug.index.the_run_has_started", "The run has started."),
        )
    }
}

/**
 * The file, in the project's source language, as a file on disk.
 *
 * `ExportCard` has done this since it existed; the overview's own menu entry navigated to the page
 * that hosts it instead, under a download glyph.
 */
private suspend fun downloadSourceFile(project: ProjectDetailDto, file: FileDto) {
    val text = ApiClient.exportFile(project.slug, file.id, project.sourceLocale)
    downloadText(text, exportFileName(project.slug, project.sourceLocale, file))
}

/** `atlas-en-messages.json`, with anything a file system would argue about replaced. */
internal fun exportFileName(slug: String, locale: String, file: FileDto): String {
    val original = file.path.substringAfterLast('/')
    val stem = original.substringBeforeLast('.', original)
    return "$slug-$locale-$stem.${file.format.extension}".replace(Regex("[^A-Za-z0-9._-]"), "-")
}

/**
 * The server's own sentence about a refused export, or this page's when there is not one.
 *
 * "This file has no keys for de" is worth more than anything the browser could invent, and a
 * `Throwable` that is not an `ApiException` carries a message written for whoever debugs it.
 */
private fun exportFailureMessage(failure: Throwable): String =
    (failure as? ApiException)?.message?.takeIf(String::isNotBlank)
        ?: tr(
            "app.projects.slug.index.couldnt_download_that_file_try_again",
            "Couldn't download that file. Try again.",
        )
